import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import { useEffect } from 'react';
import { Logger, SuperProperties } from './AnalyticsInterface';
import { getPageName, isClientSide, isServerSide, isTestRunning, VerboseLoggingOn } from './AnalyticsUtil';
import { ConsoleLogger } from './ConsoleLogger';
import { DataDogLogger } from './DatadogLogger';
import { EngagementEvents } from './EngagementEvents';
import { FirebaseLogger } from './FirebaseLogger';
import { MixpanelLogger } from './MixPanelLogger';
import packageJSON from '../../package.json';
import { getClientConfig } from '../Config/ClientConfig';

const loggers: Logger[] = [ConsoleLogger, DataDogLogger, MixpanelLogger, FirebaseLogger];

/**
 * Initialize analytics on the client's browser
 */
export const analyticsClientInit = async (session: Session | null) => {
  if (isTestRunning()) return;
  if (isServerSide()) {
    console.error('analyticsClientInit cannot be used server side');
    return;
  }

  loggers.forEach((logger) => {
    try {
      logger?.init?.();
    } catch (error) {
      console.error('Error Event: ', {
        loggerName: logger.name,
        error,
        superProps: superProps,
      });
    }
  });

  await identify(session);
};

const superProps: SuperProperties = {
  app_name: packageJSON.name,
};

const initEnvSuperProps = async () => {
  if (!superProps.env || !superProps.app_version || !superProps.system_version) {
    // Add in props to superProps that could be async to fetch
    if (isClientSide()) {
      const { env, appVersion, systemVersion } = await getClientConfig();
      superProps.env = env;
      superProps.app_version = appVersion;
      superProps.system_version = systemVersion;
    } else {
      superProps.env = process.env.ENV!;
      superProps.app_version = process.env.APP_VERSION!;
      superProps.system_version = process.env.SYSTEM_VERSION!;
    }
  }
};

export const addProp = (name: string, value?: string) => {
  superProps[name] = value || '';
};

export const identify = async (session: Session | null) => {
  if (isTestRunning()) return;
  await initEnvSuperProps();
  /**
   * Always identify if serverside due to node issues.
   * Always identify if a session is passed in, because its fast.
   * Otherwise identify only if the properties have not been added
   */
  if (isClientSide() && Object.keys(superProps).length > 5) return;

  if (!session?.user) {
    return;
  }

  addProp('user_name', session.user?.userName);
  addProp('email', session.user?.email);
  addProp('user_id', session.user?.id);
  addProp('institution_id', session.user?.institution?.id);
  addProp('institution', session.user?.institution?.name);
  addProp('user_role', session.user?.role);
  addProp('number_of_logins', session.user?.numberOfLogins?.toString());
  addProp('user_role', session.user?.role);
  addProp('last_login', session.user?.lastLogin ? new Date(session.user?.lastLogin).toLocaleTimeString() : '');

  try {
    loggers.forEach((logger) => {
      try {
        logger?.identify?.(superProps);
      } catch (error) {
        console.error('Error Event: ', {
          loggerName: logger.name,
          error,
          superProps: superProps,
        });
      }
    });
  } catch (error) {
    console.error(error);
  }
};

const getCombinedProps = (props?: any) =>
  typeof props === 'string'
    ? {
        page: getPageName(),
        ...superProps,
        props,
      }
    : { page: getPageName(), ...superProps, ...props };

export const useLogPageView = (name: EngagementEvents, props?: any) => {
  useEffect(() => {
    logKariusEvent(name, props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const logVerbose = async (name: string, props?: any): Promise<void> => {
  await logBase('verbose', name, props);
};

export const logKariusEvent = async (name: EngagementEvents, props?: any) => {
  await logBase('log', name, { karius_event: true, ...props });
};

export const logEvent = async (name: string, props?: any): Promise<void> => {
  return logBase('log', name, props);
};

export const logWarn = async (name: string, props?: any): Promise<void> => {
  return logBase('warn', name, props);
};

export const logError = async (name: string, props?: any): Promise<void> => {
  return logBase('error', name, props);
};

let currentSession: Session | null = null;

const logBase = async (severity: 'log' | 'error' | 'warn' | 'verbose', name: string, props?: any): Promise<void> => {
  if (isTestRunning()) return;
  if (severity === 'verbose' && !VerboseLoggingOn) return;

  if (isClientSide() && !currentSession) {
    // Client side has lazy initialization of logger
    // Server side it is done on each request that is wrapped withAPIMiddleware
    const sessionData = await getSession();
    if (sessionData?.user) {
      currentSession = sessionData;
    }
    await analyticsClientInit(sessionData);
  }

  const combinedProps = getCombinedProps(props);

  loggers.forEach((logger) => {
    try {
      if (severity === 'error') {
        logger?.logError?.(name, combinedProps);
      } else if (severity === 'warn') {
        logger?.logWarn?.(name, combinedProps);
      } else {
        logger?.logEvent?.(name, combinedProps);
      }
    } catch (error) {
      console.error('Error Event: ' + name, {
        loggerName: logger.name,
        error,
        props: props,
        superProps: superProps,
        combinedProps: combinedProps,
      });
    }
  });
};
